import { StyleSheet, View, ViewProps } from 'react-native'
import { AccessType, CompanyData, DataWithUpload, Draft, useCompany } from 'zens-store'
import { observer } from 'mobx-react'
import { Button, Checkbox, Color, Spacing, Text } from 'zens-storybook'
import { useMemo } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { Link } from 'zens-router'

import { usePermissions } from '@dms/hook'

import { NavigationConfirm } from '../navigation'

const styles = StyleSheet.create({
  container: {},
  content: {
    flexDirection: 'row',
  },
  main: {
    flex: 1,
    flexDirection: 'column',
    gap: Spacing.L,
  },
  buttons: {
    marginTop: Spacing.L,
    flexDirection: 'row',
    marginHorizontal: -Spacing.XS,
  },
  button: {
    marginHorizontal: Spacing.XS,
  },
  text: {
    marginTop: Spacing.S,
    color: Color.metalGrey,
  },
  field: {
    marginTop: Spacing.M,
    flexDirection: 'row',
  },
  skeletonField: {
    marginTop: Spacing.M,
  },
  skeletonDescription: {
    marginTop: Spacing.XS,
  },
})

interface Props extends ViewProps {
  companyId: number
  onSave?: (draft: Draft<DataWithUpload<CompanyData>>) => Promise<void>
  loading?: boolean
}

const CompanyRegularSyncForm = (props: Props) => {
  const { companyId, onSave, loading, style, ...otherProps } = props
  const company = useCompany({ companyId })
  const { t } = useTranslation()
  const draft = useMemo(() => new Draft<DataWithUpload<CompanyData>>({ id: 0, ...company.data?.data }), [company.data])
  const { hasPermission } = usePermissions(companyId)

  const canEdit = hasPermission(AccessType.Update, 'settings')

  function updateHasRegularSync(value: boolean) {
    draft.update('has_regular_sync', value)
  }

  if (!hasPermission(AccessType.Read, 'settings')) return null

  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        <View style={styles.main}>
          {hasPermission(AccessType.Read, 'settings') && (
            <View>
              <Text level={3}>
                <Trans>Regular sync</Trans>
              </Text>
              <Text style={styles.text}>
                <Trans>
                  Enabling the regular sync will make the PuK devices sync with the server at regular intervals. This is
                  useful for faster dispatch of new instructions to the devices. When activated, this mode applies to
                  all devices in the company.
                </Trans>
              </Text>
              <View style={styles.field}>
                <Checkbox
                  label={t('Regular sync active')}
                  value={draft.data.has_regular_sync}
                  onValueChange={updateHasRegularSync}
                  disabled={loading || !canEdit}
                  testID="CompanyRegularSyncForm.RegularSync"
                />
              </View>
            </View>
          )}
        </View>
      </View>
      <View style={styles.buttons}>
        <Button
          title={t('Save')}
          size="L"
          style={styles.button}
          onPress={() => canEdit && onSave?.(draft)}
          disabled={loading || !canEdit}
          testID="CompanyRegularSyncForm.Save"
        />
        <Link href={`/companies/${companyId}/detail`} asChild>
          <Button
            title={t('Cancel')}
            light
            size="L"
            style={styles.button}
            disabled={loading}
            testID="CompanyRegularSyncForm.Cancel"
          />
        </Link>
      </View>
      <NavigationConfirm draft={draft} />
    </View>
  )
}
interface SkeletonProps extends ViewProps {}
const Skeleton = (props: SkeletonProps) => {
  const { style, ...otherProps } = props
  const { t } = useTranslation()
  return (
    <View style={[styles.container, style]} {...otherProps}>
      <View style={styles.content}>
        <View style={styles.main}>
          <Text.Skeleton level={3} />
          <Text.Skeleton style={[styles.text, styles.skeletonDescription]} numberOfLines={2} />
          <Text.Skeleton level={5} style={styles.skeletonField} />
        </View>
      </View>
      <View style={styles.buttons}>
        <Button.Skeleton size="L" style={styles.button} />
        <Button title={t('Cancel')} size="L" style={styles.button} />
      </View>
    </View>
  )
}
CompanyRegularSyncForm.Skeleton = Skeleton

export default observer(CompanyRegularSyncForm)
